<template>
  <div class="encuesta-contenedor">
    <div class="encuesta-card">

      <div class="lang-switch">
        <button :class="{ active: locale === 'es' }" @click="setLang('es')">ES</button>
        <span class="lang-sep">|</span>
        <button :class="{ active: locale === 'en' }" @click="setLang('en')">EN</button>
      </div>

      <img class="logo" src="@/assets/imagenes/logotipo.png" alt="Domédica" />

      <template v-if="!enviada">
        <h2 class="titulo">{{ $t('survey.title') }}</h2>
        <p class="subtitulo">{{ $t('survey.subtitle') }}</p>

        <form @submit.prevent="enviarEncuesta" novalidate>

          <div class="pregunta">
            <label>{{ $t('survey.hotel_label') }}<span class="req">*</span></label>
            <select v-model="form.hotel">
              <option value="" disabled>{{ $t('survey.hotel_placeholder') }}</option>
              <option v-for="h in hoteles" :key="h.id" :value="h.nombre">{{ h.nombre }}</option>
              <option value="__otro__">{{ $t('survey.hotel_other') }}</option>
            </select>
            <input
              v-if="form.hotel === '__otro__'"
              v-model.trim="hotelOtro"
              type="text"
              maxlength="120"
              class="hotel-otro"
              :placeholder="$t('survey.hotel_other_placeholder')"
            >
          </div>

          <div class="pregunta" v-for="p in preguntasEscala" :key="p.campo">
            <label>{{ p.texto }}<span class="req">*</span></label>
            <p class="escala-hint">{{ p.hint }}</p>
            <div class="estrellas" role="radiogroup">
              <button
                v-for="n in 5"
                :key="n"
                type="button"
                class="estrella-btn"
                :aria-label="String(n)"
                @click="form[p.campo] = n"
              >
                <span class="material-icons">{{ n <= (form[p.campo] || 0) ? 'star' : 'star_border' }}</span>
              </button>
            </div>
          </div>

          <div class="pregunta">
            <label>{{ $t('survey.q_recomendacion') }}<span class="req">*</span></label>
            <p class="escala-hint">{{ $t('survey.hint_not_likely') }}</p>
            <div class="estrellas" role="radiogroup">
              <button
                v-for="n in 5"
                :key="n"
                type="button"
                class="estrella-btn"
                :aria-label="String(n)"
                @click="form.recomendacion = n"
              >
                <span class="material-icons">{{ n <= (form.recomendacion || 0) ? 'star' : 'star_border' }}</span>
              </button>
            </div>
          </div>

          <div class="pregunta">
            <label>{{ $t('survey.comments_label') }}</label>
            <textarea v-model.trim="form.comentarios" maxlength="2000" rows="4" :placeholder="$t('survey.comments_placeholder')"></textarea>
          </div>

          <button type="submit" class="btn-enviar" :disabled="enviando">
            {{ enviando ? $t('survey.sending') : $t('survey.submit') }}
          </button>
        </form>
      </template>

      <template v-else>
        <div class="agradecimiento">
          <span class="material-icons">check_circle</span>
          <h2 class="titulo">{{ $t('survey.thanks_title') }}</h2>
          <p class="subtitulo">{{ $t('survey.thanks_text') }}</p>
          <button class="btn-enviar" @click="volver">{{ $t('survey.back') }}</button>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';
import { addEncuesta, getHoteles } from '@/utils/api';
import i18n from '@/i18n';

function formVacio() {
  return {
    hotel: '',
    tiempo_espera: null,
    gafete: null,
    presentacion: null,
    atencion_medico: null,
    claridad_explicacion: null,
    nivel_ingles: null,
    calidad_precio: null,
    atencion_domedica: null,
    recomendacion: null,
    comentarios: '',
  };
}

export default {
  name: 'EncuestaSatisfaccion',
  data() {
    return {
      enviando: false,
      enviada: false,
      form: formVacio(),
      hotelOtro: '',
      locale: localStorage.getItem('sanatia_lang') || 'es',
      hoteles: [],
    };
  },
  created() {
    getHoteles()
      .then((hoteles) => { this.hoteles = hoteles.filter((h) => h.activo); })
      .catch(() => { this.hoteles = []; });
  },
  computed: {
    preguntasEscala() {
      const preguntas = [
        { campo: 'tiempo_espera', texto: this.$t('survey.q_tiempo_espera'), hint: this.$t('survey.hint_poor_excellent') },
        { campo: 'gafete', texto: this.$t('survey.q_gafete'), hint: this.$t('survey.hint_poor_excellent') },
        { campo: 'presentacion', texto: this.$t('survey.q_presentacion'), hint: this.$t('survey.hint_poor_excellent') },
        { campo: 'atencion_medico', texto: this.$t('survey.q_atencion_medico'), hint: this.$t('survey.hint_poor_excellent') },
        { campo: 'claridad_explicacion', texto: this.$t('survey.q_claridad_explicacion'), hint: this.$t('survey.hint_not_clear') },
        { campo: 'nivel_ingles', texto: this.$t('survey.q_ingles'), hint: this.$t('survey.hint_poor_excellent') },
        { campo: 'calidad_precio', texto: this.$t('survey.q_calidad_precio'), hint: this.$t('survey.hint_poor_excellent') },
        { campo: 'atencion_domedica', texto: this.$t('survey.q_atencion_domedica'), hint: this.$t('survey.hint_poor_excellent') },
      ];
      // La pregunta de nivel de inglés no aplica cuando la encuesta se responde en español.
      return this.locale === 'es' ? preguntas.filter(p => p.campo !== 'nivel_ingles') : preguntas;
    },
  },
  methods: {
    setLang(lang) {
      this.locale = lang;
      localStorage.setItem('sanatia_lang', lang);
      i18n.global.locale.value = lang;
    },
    validar() {
      const obligatorios = ['recomendacion', ...this.preguntasEscala.map(p => p.campo)];
      const hotelValido = this.form.hotel === '__otro__' ? !!this.hotelOtro : !!this.form.hotel;
      return hotelValido && obligatorios.every(campo => this.form[campo] !== null && this.form[campo] !== '');
    },
    limpiar(valor) {
      return typeof valor === 'string'
        ? Array.from(DOMPurify.sanitize(valor)).filter(c => c.charCodeAt(0) > 0x1F && c.charCodeAt(0) !== 0x7F).join('')
        : valor;
    },
    async enviarEncuesta() {
      if (!this.validar()) {
        Swal.fire(this.$t('survey.incomplete_title'), this.$t('survey.incomplete_text'), 'warning');
        return;
      }
      this.enviando = true;
      try {
        const limpio = {};
        for (const campo in this.form) limpio[campo] = this.limpiar(this.form[campo]);
        if (limpio.hotel === '__otro__') limpio.hotel = this.limpiar(this.hotelOtro);
        await addEncuesta(limpio);
        this.enviada = true;
      } catch (error) {
        Swal.fire(this.$t('survey.error_title'), error.message || this.$t('survey.error_default'), 'error');
      } finally {
        this.enviando = false;
      }
    },
    volver() {
      this.$router.push('/');
    },
  },
};
</script>

<style scoped>
.encuesta-contenedor {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 16px;
  background: var(--brand-primary);
}

.encuesta-card {
  width: min(680px, 100%);
  background: var(--white);
  border-radius: 18px;
  padding: 36px clamp(20px, 4vw, 44px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  position: relative;
}

.lang-switch {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

.lang-switch button {
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 700;
  color: var(--brand-secondary-light);
  padding: 2px 4px;
}

.lang-switch button.active {
  color: var(--brand-accent-sage);
}

.lang-sep {
  color: var(--brand-secondary-light);
}

.logo {
  display: block;
  width: 140px;
  margin: 0 auto 20px;
}

.titulo {
  color: var(--brand-primary);
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 8px;
}

.subtitulo {
  color: var(--brand-secondary);
  font-size: 14px;
  text-align: center;
  margin: 0 0 28px;
}

.req { color: #e03131; margin-left: 2px; }

.pregunta {
  margin-bottom: 24px;
}

.pregunta label {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: var(--brand-secondary);
  margin-bottom: 6px;
  line-height: 1.4;
}

.pregunta input[type="text"],
.pregunta select,
.pregunta textarea {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid var(--brand-secondary-light);
  border-radius: 8px;
  font-size: 14px;
  color: var(--brand-secondary);
  box-sizing: border-box;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
  background-color: var(--white);
}

.pregunta input[type="text"]:focus,
.pregunta select:focus,
.pregunta textarea:focus {
  outline: none;
  border-color: var(--brand-accent-sage);
  box-shadow: 0 0 0 3px rgba(96, 127, 112, 0.15);
}

.pregunta textarea { resize: vertical; }

.hotel-otro { margin-top: 10px; }

.escala-hint {
  font-size: 12px;
  color: var(--brand-secondary-light);
  margin: 0 0 10px;
}

.estrellas {
  display: flex;
  gap: 6px;
}

.estrella-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  line-height: 1;
}

.estrella-btn .material-icons {
  font-size: 32px;
  color: var(--brand-accent-sage);
  transition: transform 0.15s;
}

.estrella-btn:hover .material-icons {
  transform: scale(1.15);
}

.btn-enviar {
  display: block;
  width: 100%;
  padding: 14px;
  background: var(--brand-accent-sage);
  color: var(--white);
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.3px;
  transition: background 0.2s, transform 0.15s;
}

.btn-enviar:hover:not(:disabled) {
  background: var(--brand-accent-navy);
  transform: translateY(-2px);
}

.btn-enviar:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.agradecimiento {
  text-align: center;
  padding: 20px 0;
}

.agradecimiento .material-icons {
  font-size: 60px;
  color: var(--brand-accent-sage);
  margin-bottom: 12px;
}

.agradecimiento .btn-enviar {
  margin-top: 20px;
}

@media (max-width: 480px) {
  .encuesta-card { padding: 28px 16px; }
  .estrella-btn .material-icons { font-size: 26px; }
  .logo { width: 110px; }
}
</style>
